import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { LogIn, Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { company } from '@/constants/company'
import { NAV_ITEMS } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [activeId, setActiveId] = useState<string | null>(null)
    const isHome = router.pathname === '/'

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!isHome) {
            setActiveId(null)
            return
        }

        const sections = Array.from(document.querySelectorAll<HTMLElement>('main section[id]'))

        if (sections.length === 0) return

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) setActiveId(entry.target.id)
            }
        }, { rootMargin: '-45% 0px -50% 0px' })

        sections.forEach((section) => observer.observe(section))

        return () => observer.disconnect()
    }, [isHome])

    return (
        <header className='sticky top-0 z-50 px-3 pt-3 sm:px-5'>
            <div
                className={cn(
                    'mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 rounded-full border bg-card/85 pl-4 pr-2 backdrop-blur-xl transition-[box-shadow,border-color] duration-300 sm:pl-5',
                    scrolled || open
                        ? 'border-border shadow-[0_1px_2px_rgb(18_28_26/0.05),0_14px_34px_-18px_rgb(18_28_26/0.28)]'
                        : 'border-border/70 shadow-[0_1px_2px_rgb(18_28_26/0.04)]'
                )}
            >
                <Link
                    href='/'
                    aria-label='ConcreTrack, zur Startseite'
                    className='rounded-full py-1 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                >
                    <Logo />
                </Link>

                <nav aria-label='Hauptnavigation' className='hidden lg:block'>
                    <ul className='flex items-center gap-0.5'>
                        {NAV_ITEMS.map((item) => {
                            const active = activeId === item.id

                            return (
                                <li key={item.id} className={cn(item.wide && 'hidden xl:block')}>
                                    <Link
                                        href={`/#${item.id}`}
                                        aria-current={active ? 'true' : undefined}
                                        className={cn(
                                            'inline-flex rounded-full px-3.5 py-2 text-[0.94rem] transition-colors',
                                            active
                                                ? 'bg-accent text-accent-foreground'
                                                : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-1.5'>
                    <a
                        href={siteConfig.loginUrl}
                        className='hidden h-11 items-center gap-2 rounded-full px-4 text-[0.94rem] font-medium text-foreground/85 transition-colors hover:bg-muted hover:text-foreground sm:inline-flex'
                    >
                        <LogIn className='size-4' aria-hidden='true' />
                        Anmelden
                    </a>

                    <Link
                        href='/#kontakt'
                        className='hidden h-11 items-center rounded-full bg-primary px-5 text-[0.94rem] font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:inline-flex'
                    >
                        Projekt anfragen
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen((value) => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-navigation'
                        aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
                        className='inline-flex size-11 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted lg:hidden'
                    >
                        {open
                            ? <X className='size-5' aria-hidden='true' />
                            : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    id='mobile-navigation'
                    className='mx-auto mt-2 max-w-6xl rounded-3xl border border-border bg-card p-3 shadow-[0_24px_48px_-24px_rgb(18_28_26/0.35)] animate-in fade-in slide-in-from-top-2 duration-200 lg:hidden'
                >
                    <nav aria-label='Menü'>
                        <ul className='grid gap-0.5'>
                            {NAV_ITEMS.map((item) => (
                                <li key={item.id}>
                                    <Link
                                        href={`/#${item.id}`}
                                        onClick={() => setOpen(false)}
                                        className='flex rounded-2xl px-4 py-3 text-base text-foreground transition-colors hover:bg-muted'
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='mt-3 grid gap-2 border-t border-border pt-3 sm:grid-cols-2'>
                        <Link
                            href='/#kontakt'
                            onClick={() => setOpen(false)}
                            className='inline-flex h-12 items-center justify-center rounded-full bg-primary px-5 font-medium text-primary-foreground transition-colors hover:bg-primary/90'
                        >
                            Projekt anfragen
                        </Link>

                        <a
                            href={siteConfig.loginUrl}
                            className='inline-flex h-12 items-center justify-center gap-2 rounded-full border border-border px-5 font-medium text-foreground transition-colors hover:bg-muted'
                        >
                            <LogIn className='size-4' aria-hidden='true' />
                            Anmelden
                        </a>

                        <a
                            href={company.phoneHref}
                            className='inline-flex h-12 items-center justify-center gap-2 rounded-full px-5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:col-span-2'
                        >
                            <Phone className='size-4' aria-hidden='true' />
                            {company.phone}
                        </a>
                    </div>
                </div>
            )}
        </header>
    )
}
